import React, { useState, useEffect } from 'react';
import { Container, Typography, Grid, Box, Alert, Chip, Breadcrumbs, Link, Card, CardContent, CardMedia } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import CategoryCard from '../components/CategoryCard';
import Loading from '../components/Loading';
import { categoryService } from '../services/api';
import { FaTh, FaLightbulb, FaBolt, FaShieldAlt, FaStar, FaBox } from 'react-icons/fa';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await categoryService.getAll();
        setCategories(response.data);
        setError(null);
      } catch (err) {
        setError('Impossible de charger les catégories.');
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const getIcon = (titre) => {
    const lower = titre.toLowerCase();
    if (lower.includes('phare') || lower.includes('led')) return <FaLightbulb size={48} />;
    if (lower.includes('feu') || lower.includes('arrière')) return <FaShieldAlt size={48} />;
    if (lower.includes('clignotant')) return <FaBolt size={48} />;
    if (lower.includes('gyrophare') || lower.includes('rotatif')) return <FaStar size={48} />;
    return <FaBox size={48} />;
  };

  const colors = ['#0077B8', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899'];

  if (loading) return <Loading />;

  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0077B8 0%, #00ACEA 100%)',
          py: { xs: 6, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 30%, rgba(251, 191, 36, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
            `,
            opacity: 0.5,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs 
            sx={{ 
              mb: 3,
              '& .MuiBreadcrumbs-separator': { 
                color: 'rgba(255, 255, 255, 0.6)',
              }
            }}
          >
            <Link 
              component={RouterLink} 
              to="/" 
              underline="hover" 
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.875rem',
                '&:hover': { color: '#fbbf24' }
              }}
            >
              Accueil
            </Link>
            <Typography 
              sx={{ 
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              Catalogue
            </Typography>
          </Breadcrumbs>

          {/* Title Section */}
          <Box sx={{ textAlign: 'center', maxWidth: '900px', mx: 'auto' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'rgba(251, 191, 36, 0.2)',
                border: '1px solid rgba(251, 191, 36, 0.4)',
                borderRadius: '30px',
                px: 2.5,
                py: 0.75,
                mb: 3,
              }}
            >
              <FaTh size={16} color="#fbbf24" />
              <Typography
                sx={{
                  color: '#fbbf24',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  letterSpacing: '0.5px',
                }}
              >
                NOTRE CATALOGUE
              </Typography>
            </Box>

            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                fontWeight: 900,
                color: 'white',
                mb: 2,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              Catégories de Produits
            </Typography>
            
            <Typography 
              sx={{ 
                fontSize: { xs: '1rem', md: '1.188rem' },
                color: 'rgba(255, 255, 255, 0.9)',
                maxWidth: '700px',
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              Explorez notre gamme complète d'équipements d'éclairage professionnel pour véhicules. Chaque catégorie est conçue pour répondre à des besoins spécifiques.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Categories Grid */}
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 10 } }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 4,
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.15)',
            }}
          >
            {error}
          </Alert>
        )}

        {categories.length === 0 && !error ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '24px',
                background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                boxShadow: '0 12px 32px rgba(14, 165, 233, 0.3)',
              }}
            >
              <FaTh size={56} color="white" />
            </Box>
            <Typography 
              variant="h5" 
              fontWeight={700} 
              gutterBottom
              sx={{ color: '#0c4a6e' }}
            >
              Aucune catégorie disponible
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ maxWidth: 400, mx: 'auto' }}
            >
              Les catégories de produits seront bientôt disponibles. Revenez nous voir prochainement !
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {categories.map((category, index) => {
              const categoryColor = colors[index % colors.length];
              
              return (
                <Grid 
                  item 
                  xs={12} 
                  sm={6} 
                  md={4} 
                  key={category.id}
                  sx={{
                    animation: `fadeInScale 0.6s ease-out ${index * 0.08}s both`,
                    '@keyframes fadeInScale': {
                      '0%': {
                        opacity: 0,
                        transform: 'scale(0.9) translateY(20px)',
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'scale(1) translateY(0)',
                      },
                    },
                  }}
                >
                  <Card
                    onClick={() => navigate(`/catalogue?category=${category.id}`)}
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      borderRadius: 4,
                      overflow: 'hidden',
                      background: 'white',
                      border: '2px solid',
                      borderColor: 'rgba(14, 165, 233, 0.12)',
                      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '5px',
                        background: `linear-gradient(90deg, ${categoryColor}, ${categoryColor}cc)`,
                        opacity: 0.8,
                      },
                      '&:hover': {
                        transform: 'translateY(-12px) scale(1.02)',
                        borderColor: categoryColor,
                        boxShadow: `0 20px 48px ${categoryColor}40`,
                        '& .category-icon-box': {
                          transform: 'scale(1.15) rotate(5deg)',
                          background: categoryColor,
                        },
                        '& .category-icon': {
                          color: 'white',
                        },
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Box
                        className="category-icon-box"
                        sx={{
                          width: 110,
                          height: 110,
                          borderRadius: '24px',
                          background: `${categoryColor}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 24px',
                          boxShadow: `0 8px 24px ${categoryColor}25`,
                          transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                          border: `2px solid ${categoryColor}30`,
                        }}
                      >
                        <Box
                          className="category-icon"
                          sx={{
                            color: categoryColor,
                            display: 'flex',
                            transition: 'color 0.5s ease',
                          }}
                        >
                          {getIcon(category.titre)}
                        </Box>
                      </Box>
                      
                      <Typography 
                        variant="h5" 
                        fontWeight={800} 
                        gutterBottom 
                        sx={{ 
                          color: '#0c4a6e', 
                          mb: 2,
                          fontSize: '1.375rem',
                        }}
                      >
                        {category.titre}
                      </Typography>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 3, 
                          lineHeight: 1.7,
                          fontSize: '0.938rem',
                          minHeight: '60px',
                        }}
                      >
                        {category.description || 'Découvrez notre sélection de produits professionnels'}
                      </Typography>
                      
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1,
                          bgcolor: `${categoryColor}15`,
                          color: categoryColor,
                          px: 2.5,
                          py: 1,
                          borderRadius: 2,
                          border: `1.5px solid ${categoryColor}30`,
                          fontSize: '0.875rem',
                          fontWeight: 800,
                        }}
                      >
                        <FaBox size={14} />
                        {category.product_count || 0} produit{(category.product_count || 0) > 1 ? 's' : ''}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default Categories;
